<template>
  <div class="panel panel-default panel-top-bordered">
    <div v-if="headline" class="panel-heading">
      <h5 class="pull-left">
        {{ headline }}
      </h5>

      <div class="pull-right">
        <slot name="headline-right"/>
      </div>

      <div class="clearfix"/>
    </div>

    <div class="panel-body">
      <inline-form
          :value="filterValues"
          :fields="filters"
          @click="$event === 'clear' && $emit('clear-filters')"
          @input="$emit('update:filter-values', $event)"
          @submit="$emit('navigate', 1, pageSize, filterValues)"
      ></inline-form>

      <loading-spinner v-if="loading"></loading-spinner>

      <div v-else>
        <div v-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <div v-else>
          <table v-if="data.length" class="table table-condensed table-striped">
            <thead>
            <tr>
              <th v-if="showCheckboxes || reorderable" class="text-center" :class="'col-xs-'+checkboxColumnWidth">
                <input
                    v-if="showCheckboxes && !reorderable"
                    type="checkbox"
                    v-model="selectAll"
                    :indeterminate.prop="hasSelected && selected.length && selected.length !== data.length"
                    :disabled="!hasSelected"
                >
              </th>

              <th v-for="headline in headlines" :class="'col-xs-' + headline.width + ' ' + headline.class"
                  style="font-size: 11px">
                <a
                    v-if="headline.sort_column" href="#"
                    @click.prevent="$emit('sort', headline.sort_column)"
                >
                  <abbr v-if="headline.description" :title="headline.description">
                    {{ headline.text }} <i class="fa fa-sort"></i>
                  </abbr>

                  <template v-else>
                    {{ headline.text }} <i class="fa fa-sort"></i>
                  </template>
                </a>

                <abbr v-else-if="headline.description" :title="headline.description">
                  {{ headline.text }}
                </abbr>

                <template v-else>
                  {{ headline.text }}
                </template>
              </th>

              <th v-if="showButtons" :class="'col-xs-' + buttonColumnWidth"></th>
            </tr>
            </thead>

            <Draggable
                element="tbody"
                :value="data"
                handle=".handle"
                chosen-class="interval--dragging"
                @input="$emit('reordered', $event)"
            >
              <tr v-for="row in data" :class="getRowClass(row)" :style="getRowStyle(row)">
                <td v-if="showCheckboxes || reorderable" class="text-center">
                  <input
                      v-if="showCheckboxes && !reorderable"
                      type="checkbox"
                      v-model="computedSelected"
                      :value="row" :disabled="!hasSelected"
                  >

                  <i
                      v-if="reorderable"
                      class="fa fa-arrows-alt"
                      style="cursor: grab;"
                      :class="{'handle': canSort}"
                      :style="{'color': canSort ? 'inherit' : '#a3abaf'}"
                  ></i>
                </td>

                <slot :row="row"></slot>

                <td v-if="showButtons" class="text-right">
                  <div class="btn-group list-actions">
                    <button
                        type="button"
                        class="btn btn-sm"
                        :disabled="!canShow(row)"
                        @click="$emit('show', row)"
                    >
                      <i class="fa fa-search"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-sm"
                        :disabled="!canDestroy(row)"
                        @click="$emit('destroy', row)"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </Draggable>

            <slot name="foot" :data="data"/>
          </table>

          <div v-else class="text-center" style="padding: 40px;">
            <slot name="empty">
              <i>
                {{ emptyMessage }}
              </i>
            </slot>
          </div>

          <div class="tw-flex tw-justify-between">
            <div style="display: flex">
              <div v-if="showListActions && !reorderable" class="dropup">
                <button
                    class="btn btn-black btn-sm dropdown-toggle"
                    type="button"
                    data-toggle="dropdown"
                    :disabled="!hasSelected"
                >
                  <i class="fa fa-cogs"></i> List Actions <span class="caret"></span>
                </button>

                <ul class="dropdown-menu">
                  <slot name="list-actions">
                    <li class="disabled">
                      <a href="#" @click.prevent="">
                        No actions available
                      </a>
                    </li>
                  </slot>
                </ul>
              </div>

              <SelectWidget
                  v-if="pagination && pageSizeOptions"
                  class="input-sm"
                  :value="pageSize"
                  :options="pageSizeOptions.map(value => ({value, label: value + ' per page'}))"
                  style="margin-left: 5px; width: auto;"
                  @input="$emit('update:page-size', $event)"
              />
            </div>

            <div>
              <resource-pager
                  class="table__pager"
                  v-if="pagination && pagination.last_page > 1"
                  :pagination="pagination"
                  @navigate="$emit('navigate', $event)"
              />
            </div>

            <div>
              <slot name="bottom">
                <div v-if="pagination && data.length" class="text-right" style="padding: 5px 0">
                  <span v-if="selected.length"><b>{{ selected.length }}</b> selected &middot; </span>Showing
                  <b>{{ pagination.from }}</b> to <b>{{ pagination.to }}</b> of <b>{{ pagination.total }}</b> results
                </div>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>

    <slot name="modals" :data="data"/>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import InlineForm from '@/components/Forms/InlineForm'
import LoadingSpinner from '@/components/Layout/LoadingSpinner'
import ResourcePager from '@/components/Utilities/Pagination/ResourcePager'
import store from '@/store'
import SelectWidget from '../../../vendor/forms/FormGroups/Widgets/Select'

export default {
  components: {SelectWidget, Draggable, InlineForm, LoadingSpinner, ResourcePager},

  props: {
    headline: {
      required: false,
      default: null
    },

    headlines: {
      required: true,
      type: Array
    },

    filters: {
      required: false,
      type: Array,
      default: () => []
    },

    filterValues: {
      required: false,
      type: Object,
      default: () => ({})
    },

    data: {
      required: true,
      type: Array
    },

    pagination: {
      required: false,
      type: Object
    },

    loading: {
      required: false,
      type: Boolean,
      default: false
    },

    error: {
      required: false,
      type: String,
      default: null
    },

    emptyMessage: {
      required: false,
      type: String,
      default: 'The list is empty.'
    },

    canShow: {
      required: false,
      type: Function,
      default: row => true
    },

    canDestroy: {
      required: false,
      type: Function,
      default: row => true
    },

    getRowClass: {
      required: false,
      type: Function,
      default: row => ''
    },

    getRowStyle: {
      required: false,
      type: Function,
      default: row => ''
    },

    selected: {
      required: false,
      type: Array,
      default: null
    },

    canDestroySelected: {
      required: false,
      type: Boolean,
      default: false
    },

    showCheckboxes: {
      required: false,
      type: Boolean,
      default: true
    },

    showButtons: {
      required: false,
      type: Boolean,
      default: true
    },

    showListActions: {
      required: false,
      type: Boolean,
      default: true
    },

    reorderable: {
      required: false,
      type: Boolean,
      default: false
    },

    canSort: {
      required: false,
      type: Boolean,
      default: true
    },

    checkboxColumnWidth: {
      required: false,
      type: Number,
      default: 1
    },

    buttonColumnWidth: {
      required: false,
      type: Number,
      default: 1
    },

    pageSize: {
      required: false,
      type: [Number, String],
      default: 25
    },

    pageSizeOptions: {
      required: false,
      type: Array,
      default: () => [10, 25, 50, 100]
    }
  },

  data() {
    return {
      selectAll: false,

      shared: store
    }
  },

  watch: {
    selected() {
      if (this.selected.length === this.data.length) {
        this.selectAll = true
      }

      if (this.selected.length === 0) {
        this.selectAll = false
      }
    },

    selectAll(value) {
      this.$emit('update:selected', value ? this.data : [])
    }
  },

  computed: {
    hasSelected() {
      return this.selected !== null
    },

    computedSelected: {
      get() {
        return this.selected
      },

      set(value) {
        this.$emit('update:selected', value)
      }
    }
  },

  methods: {
    updateFilters(key, value) {
      this.$emit('update:filter-values', {...this.filterValues, [key]: value})
    }
  }
}
</script>

<style>
.table__ellipsis {
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table__pager .pager {
  margin: 0
}
</style>
